<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue';
import DataTable, { DataTableFilterEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import { FilterMatchMode } from '@primevue/core/api';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import { useEndpoint } from '@apparatix/composables';

interface LightingOptionAssignmentProps {
  lightOption: any;
  isCapability: boolean
  assignedSet: Set<number>;
  unassignedSet: Set<number>;
}

const filtersAssigned = ref({
  'invNumber': { value: null, matchMode: FilterMatchMode.CONTAINS },
  'invStatus': { value: [], matchMode: FilterMatchMode.IN },
  'invMarket': { value: null, matchMode: FilterMatchMode.IN },
  'invType': { value: null, matchMode: FilterMatchMode.IN },
  'capability.id': { value: [], matchMode: FilterMatchMode.IN },
  'contract.id': { value: [], matchMode: FilterMatchMode.IN },
});

const filtersUnassigned = ref({
  'invNumber': { value: null, matchMode: FilterMatchMode.CONTAINS },
  'invStatus': { value: [], matchMode: FilterMatchMode.IN },
  'invMarket': { value: null, matchMode: FilterMatchMode.IN },
  'invType': { value: null, matchMode: FilterMatchMode.IN },
  'capability.id': { value: [], matchMode: FilterMatchMode.IN },
  'contract.id': { value: [], matchMode: FilterMatchMode.IN },
});

const props = defineProps<LightingOptionAssignmentProps>();
const isLoading = ref(false);

const faces = ref<any>([{}]);

const filteredAssigned = ref([]);
const filteredUnassigned = ref([]);

const selectedAssign = ref();
const selectedUnassign = ref();

watch(selectedAssign, (newValue) => {
  if (! newValue.length) {
    return;
  }

  newValue.forEach((selectedFace) => {
    const index = faces.value.findIndex(face => face.invNumber === selectedFace.invNumber);
    if (props.unassignedSet.has(faces.value[index].invId)) {
      props.unassignedSet.delete(faces.value[index].invId);
    } else {
      props.assignedSet.add(faces.value[index].invId);
    }

    if (props.isCapability) {
      faces.value[index].capability = props.lightOption;
    } else {
      faces.value[index].contract = props.lightOption;
    }
  });

  selectedAssign.value = [];
});

watch(selectedUnassign, (newValue) => {
  if (! newValue.length) {
    return;
  }

  newValue.forEach((selectedFace) => {
    const index = faces.value.findIndex(face => face.invNumber === selectedFace.invNumber);
    if (props.assignedSet.has(faces.value[index].invId)) {
      props.assignedSet.delete(faces.value[index].invId);
    } else {
      props.unassignedSet.add(faces.value[index].invId);
    }
    if (props.isCapability) {
      faces.value[index].capability = null;
    } else {
      faces.value[index].contract = null;
    }
  });

  selectedUnassign.value = [];
});


// Filter Section Start
const markets = computed(() => {
  const _markets = faces.value.map(face => face['invMarket']);
  return [... new Set(_markets)];
});

const statuses = computed(() => {
  const _statuses = faces.value
    .map(face => face['invStatus'])
    .filter(status => status);
  return [... new Set(_statuses)];
});

const types = computed(() => {
  const invTypes = faces.value.map(face => face['invType']);
  return [... new Set(invTypes)];
});


const capabilityOptions = computed(() => {
  const allCapabilities = faces.value.map(face => face.capability);

  const uniqueMap = new Map();

  for (const cap of allCapabilities) {
    if (!cap) continue;
    if (!uniqueMap.has(cap.id)) {
      uniqueMap.set(cap.id, { displayName: cap.displayName, id: cap.id });
    }
  }

  const options = Array.from(uniqueMap.values());

  const hasUnassigned = allCapabilities.some(cap => !cap);
  if (hasUnassigned) {
    options.unshift({ displayName: 'No Lighting', id: null });
  }

  return options;
});

const contractOptions = computed(() => {
  const allContracts = faces.value.map(face => face.contract);

  const uniqueMap = new Map();

  for (const cap of allContracts) {
    if (!cap) continue;
    if (!uniqueMap.has(cap.id)) {
      uniqueMap.set(cap.id, { displayName: cap.displayName, id: cap.id });
    }
  }

  const options = Array.from(uniqueMap.values());

  const hasUnassigned = allContracts.some(cap => !cap);
  if (hasUnassigned) {
    options.unshift({ displayName: 'No Contract', id: null });
  }

  return options;
});
// Filter Section End


const assignedCodes = computed(() => faces.value.filter(face => {
  if (props.isCapability) {
    return face.capability?.id === props.lightOption.id;
  }
  return face.contract?.id === props.lightOption.id;
}));


const unassignedCodes = computed(() => faces.value.filter(face => {
  if (props.isCapability) {
    return face.capability?.id !== props.lightOption.id;
  }
  return face.contract?.id !== props.lightOption.id;
}));

const { makeRequest: makeGetLightingAssignmentRequest } = useEndpoint({
  type: 'GET',
  url: '/lightingConfiguration/assignments',
  successField: 'data',
  onSuccess: response => {
    faces.value = response.data.inventoryLighting;
  },
  toast: { showOnSuccess: false },
});


const loadData = async () => {
  isLoading.value = true;
  await makeGetLightingAssignmentRequest();
  isLoading.value = false;
};

const onAssignClick = (face) => {selectedAssign.value = [face];};
const onAssignAllClick = () => {selectedAssign.value = filteredUnassigned.value;};
const onFilterAssigned = (e: DataTableFilterEvent) => {
  filteredAssigned.value = e.filteredValue;
};

const onUnassignClick = (face) => {selectedUnassign.value = [face];};
const onUnassignAllClick = () => {selectedUnassign.value = filteredAssigned.value;};
const onFilterUnassigned = (e: DataTableFilterEvent) => {
  filteredUnassigned.value = e.filteredValue;
};

onMounted(() => loadData());

</script>

<template>
  <FormSkeleton v-if="isLoading" />
  <DataTable
    v-else
    v-model:filters="filtersUnassigned"
    v-model:selection="selectedAssign"
    :value="unassignedCodes"
    size="small"
    data-key="invId"
    filter-display="row"
    striped-rows
    paginator
    :rows="5"
    :rows-per-page-options="[5, 10, 25, 100, 500, 1000, 5000]"
    table-style="min-width: 50rem;"
    removable-sort
    class="pt-2 px-2"
    @filter="onFilterUnassigned"
  >
    <template #header>
      <div class="flex flex-nowrap items-center gap-2">
        <span class="text-xl text-900 font-bold">Active Faces</span>
        <pv-badge :value="filteredUnassigned.length" severity="secondary" />
      </div>
    </template>
    <template #empty>
      No Faces found.
    </template>
    <Column
      field="invNumber"
      header="Face"
      sortable
      :show-filter-menu="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Faces"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="invStatus"
      header="Status"
      sortable
      :show-filter-menu="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="statuses"
          filter
          placeholder="Status"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="invMarket"
      header="Market"
      sortable
      :show-filter-menu="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="markets"
          filter
          placeholder="Markets"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="invType"
      header="Type"
      :show-filter-menu="false"
      sortable
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="types"
          placeholder="Types"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="capability.displayName"
      :filter-field="'capability.id'"
      header="Capability"
      :show-filter-menu="false"
      sortable
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="capabilityOptions"
          option-label="displayName"
          option-value="id"
          placeholder="Capabilities"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="contract.displayName"
      :filter-field="'contract.id'"
      header="Contract"
      :show-filter-menu="false"
      sortable
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="contractOptions"
          option-label="displayName"
          option-value="id"
          placeholder="Contracts"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column header-style="min-width: 3rem; max-width: 3rem" :show-filter-menu="false">
      <template #filter="{}">
        <Button
          v-tooltip.left="'Add Filtered Faces'"
          size="small"
          severity="primary"
          outlined
          label="Add All"
          style="width: max-content; font-weight: 400;"
          @click="onAssignAllClick()"
        />
      </template>
      <template #body="slotProps">
        <Button
          size="small"
          severity="primary"
          outlined
          label="Add"
          style="height: 25px; font-weight: 400;"
          @click="onAssignClick(slotProps.data)"
        />
      </template>
    </Column>
  </DataTable>

  <FormSkeleton v-if="isLoading" />
  <DataTable
    v-else
    v-model:filters="filtersAssigned"
    v-model:selection="selectedUnassign"
    :value="assignedCodes"
    size="small"
    data-key="invId"
    filter-display="row"
    striped-rows
    paginator
    :rows="5"
    :rows-per-page-options="[5, 10, 25, 100, 500, 1000, 5000]"
    table-style="min-width: 50rem"
    removable-sort
    class="pt-2 px-2"
    @filter="onFilterAssigned"
  >
    <template #header>
      <div class="flex flex-nowrap items-center gap-2">
        <span class="text-xl text-900 font-bold">Assigned Faces</span>
        <pv-badge :value="filteredAssigned.length" severity="secondary" />
      </div>
    </template>
    <template #empty>
      No Faces found.
    </template>
    <Column
      field="invNumber"
      header="Face"
      sortable
      :show-filter-menu="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Faces"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="invStatus"
      header="Status"
      sortable
      :show-filter-menu="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="statuses"
          filter
          placeholder="Status"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="invMarket"
      header="Market"
      sortable
      :show-filter-menu="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="markets"
          filter
          placeholder="Markets"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="invType"
      header="Type"
      :show-filter-menu="false"
      sortable
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="types"
          placeholder="Types"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="capability.displayName"
      :filter-field="'capability.id'"
      header="Capability"
      :show-filter-menu="false"
      sortable
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="capabilityOptions"
          option-label="displayName"
          option-value="id"
          placeholder="Capabilities"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="contract.displayName"
      :filter-field="'contract.id'"
      header="Contract"
      :show-filter-menu="false"
      sortable
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          :options="contractOptions"
          option-label="displayName"
          option-value="id"
          placeholder="Contracts"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column header-style="min-width: 3rem; max-width: 3rem" :show-filter-menu="false">
      <template #filter="{}">
        <Button
          v-tooltip.left="'Remove Filtered Faces'"
          size="small"
          severity="primary"
          outlined
          label="Remove All"
          style="width: max-content; font-weight: 400;"
          @click="onUnassignAllClick()"
        />
      </template>
      <template #body="slotProps">
        <Button
          size="small"
          severity="primary"
          outlined
          label="Remove"
          style="height: 25px; font-weight: 400;"
          @click="onUnassignClick(slotProps.data)"
        />
      </template>
    </Column>
  </DataTable>
</template>

<style scoped>
    .custom-table-bg tr {
      background: red !important;
    }

  :deep(.p-checkbox .p-checkbox-box.p-highlight) {
    border: none;
    background: var(--primary-blue);
  }


  :deep(.p-datatable-header) {
  background: none;
  border: none;
}

  .p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}


</style>
